import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// How long the confirmation tick stays before reverting to the copy icon.
const CONFIRM_MS = 1500;

export interface CopyableValueProps {
  label: string;
  value: string;
  // Renders the value in a mono, bordered chip — for an opaque identifier, where
  // character shapes matter and the bounds help select it visually.
  mono?: boolean;
  // Accessible name for the copy button. Defaults to `Copy <label>`.
  copyLabel?: string;
  className?: string;
}

// A read-only settings row: label on the left, value and a copy button on the
// right.
//
// Confirmation is a tick on the button itself rather than a toast — it sits next
// to what was copied, so it needs no words, and copying an ID is not worth a
// notification.
export function CopyableValue({
  label,
  value,
  mono = false,
  copyLabel,
  className,
}: CopyableValueProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear on unmount so a pending revert can't fire against a gone component.
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), CONFIRM_MS);
    } catch {
      // Clipboard access can be denied (insecure context, permissions policy).
      // The value is on screen and selectable, so failing quietly beats an alert.
    }
  };

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3",
        className
      )}
    >
      <span className="text-muted-foreground text-sm">{label}</span>

      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            "truncate text-sm",
            mono && "bg-muted/50 rounded-md border px-2 py-1 font-mono text-xs"
          )}
          title={value}
        >
          {value}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={copy}
          // The name changes with the state, so a screen reader hears the
          // confirmation the tick conveys visually.
          aria-label={
            copied ? `${label} copied` : (copyLabel ?? `Copy ${label}`)
          }
          className="text-muted-foreground hover:text-foreground size-7 shrink-0"
        >
          {copied ? (
            <Check className="text-success size-4" />
          ) : (
            <Copy className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
